import React, { useMemo } from "react";
import { Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useSearchParams } from "react-router-dom";
import {
  Section,
  SectionHeader,
  NoDataText,
  HorizontalPropertyRow,
  PropertyLabel,
  PropertyValue,
  WarningIcon,
} from "../../components/SectionComponents";
import Link from "../../components/Link";
import {
  ConstraintViolationWithContext,
  collectLiveProcesses,
  useViolationSummary,
} from "./validationState";

type ErrorsSectionProps = {
  activeItem: ItemWithBOM | null;
  onNavigateToTab: (tabName: string) => void;
  sectionRef?: React.RefObject<HTMLDivElement>;
};

const ErrorMessage = styled(Typography)(() => ({
  fontSize: "14px",
  fontWeight: 400,
  color: "#FB8C00",
}));

// Steps share parameter names, so their rows are prefixed with the step.
const stepLabel = (process: Process) =>
  process.name || process.process_type?.name || process.arena_part_number;

const ErrorsSection = ({
  activeItem,
  onNavigateToTab,
  sectionRef,
}: ErrorsSectionProps) => {
  // Subscribe to violation changes using the summary hook
  const violationSummary = useViolationSummary();

  // Get current path from URL params
  const [searchParams] = useSearchParams();
  const currentPath = searchParams.get("bom-item") || "";

  // Every live process (top-level and nested steps) whose parameters may
  // have violations. Steps match violations by uuid (index -1).
  const liveProcesses = useMemo(() => {
    if (!activeItem) {
      return [];
    }
    return collectLiveProcesses(activeItem)
      .filter(({ processItem }) => processItem.process.uuid)
      .map(({ processItem, processPath }) => ({
        process: processItem.process,
        processPath,
        processIndex: processPath.length === 1 ? processPath[0] : -1,
      }));
  }, [activeItem]);

  // Get all violations for the active item using path-based lookup
  const relevantViolations = useMemo(() => {
    if (!activeItem) {
      return [];
    }
    // Use path-based lookup for reliable identification
    return violationSummary.getItemViolationsByPath(currentPath);
  }, [activeItem, currentPath, violationSummary]);

  // Extract attribute violations and their names
  const attributeViolations = useMemo(() => {
    const violations = relevantViolations.filter(
      (v) =>
        (v.attribute_name || v.context?.attributeName) &&
        !v.parameter_name &&
        !v.context?.parameterName
    );

    const grouped = new Map<string, ConstraintViolationWithContext[]>();

    violations.forEach((violation) => {
      const attrName =
        violation.attribute_name || violation.context?.attributeName;
      if (attrName) {
        if (!grouped.has(attrName)) {
          grouped.set(attrName, []);
        }
        grouped.get(attrName)!.push(violation);
      }
    });

    return Array.from(grouped.entries()).sort((a, b) =>
      a[0].localeCompare(b[0])
    );
  }, [relevantViolations]);

  // Extract process parameter violations
  const processParameterViolations = useMemo(() => {
    const result: Array<{
      process: Process;
      processPath: number[];
      rootTypeName: string;
      parameterName: string;
      violations: ConstraintViolationWithContext[];
    }> = [];

    liveProcesses.forEach(({ process, processPath, processIndex }) => {
      // Get all parameter violations for this process using path-based lookup
      // We need to check all parameters that might have violations
      const allParameterNames = new Set<string>();

      // First, get parameter names from the process definition
      if (process.process_type?.parameters) {
        process.process_type.parameters.forEach((param) => {
          allParameterNames.add(param.name);
        });
      }

      // Also check actual parameters on the process
      if (process.parameters) {
        Object.keys(process.parameters).forEach((paramName) => {
          allParameterNames.add(paramName);
        });
      }

      const rootProcess = activeItem?.processes?.[processPath[0]]?.process;
      const rootTypeName = rootProcess?.process_type?.name || "";

      // For each parameter, check if there are violations using path-based lookup
      allParameterNames.forEach((paramName) => {
        const violations = violationSummary.getParameterViolationsByPath(
          currentPath,
          processIndex,
          paramName,
          process.uuid
        );

        if (violations.length > 0) {
          result.push({
            process,
            processPath,
            rootTypeName,
            parameterName: paramName,
            violations: violations,
          });
        }
      });
    });

    // Group by top-level process type (as before), keeping each top-level
    // process's steps in tree order beneath it.
    const comparePaths = (a: number[], b: number[]) => {
      for (let i = 0; i < Math.min(a.length, b.length); i++) {
        if (a[i] !== b[i]) return a[i] - b[i];
      }
      return a.length - b.length;
    };
    return result.sort(
      (a, b) =>
        a.rootTypeName.localeCompare(b.rootTypeName) ||
        comparePaths(a.processPath, b.processPath)
    );
  }, [liveProcesses, activeItem, violationSummary, currentPath]);

  // Get item-level violations (not attribute or parameter specific)
  const itemLevelViolations = useMemo(() => {
    return relevantViolations.filter(
      (violation) =>
        !violation.attribute_name &&
        !violation.context?.attributeName &&
        !violation.parameter_name &&
        !violation.context?.parameterName
    );
  }, [relevantViolations]);

  if (!activeItem) {
    return (
      <Section ref={sectionRef}>
        <SectionHeader>Errors</SectionHeader>
        <NoDataText>Select an item to view errors.</NoDataText>
      </Section>
    );
  }

  const hasAttributeErrors = attributeViolations.length > 0;
  const hasParameterErrors = processParameterViolations.length > 0;
  const hasItemErrors = itemLevelViolations.length > 0;

  if (!hasAttributeErrors && !hasParameterErrors && !hasItemErrors) {
    return (
      <Section ref={sectionRef}>
        <SectionHeader>Errors</SectionHeader>
        <NoDataText>No errors</NoDataText>
      </Section>
    );
  }

  return (
    <Section ref={sectionRef}>
      <SectionHeader>Errors</SectionHeader>

      {hasAttributeErrors &&
        attributeViolations.map(([attributeName, violations]) => (
          <HorizontalPropertyRow key={`attribute-${attributeName}`}>
            <WarningIcon />
            <PropertyLabel>
              <Link
                onClick={(event) => {
                  event.preventDefault();
                  onNavigateToTab("attributes");
                }}
              >
                {attributeName}
              </Link>
            </PropertyLabel>
            <PropertyValue>
              <ErrorMessage>
                {violations.map((v) => v.message).join(", ")}
              </ErrorMessage>
            </PropertyValue>
          </HorizontalPropertyRow>
        ))}

      {hasParameterErrors &&
        processParameterViolations.map(
          ({ process, processPath, parameterName, violations }) => (
            <HorizontalPropertyRow
              key={`parameter-${processPath.join(".")}-${parameterName}`}
            >
              <WarningIcon />
              <PropertyLabel>
                <Link
                  href="#"
                  onClick={(event) => {
                    event.preventDefault();
                    onNavigateToTab("processes");
                  }}
                >
                  {processPath.length > 1
                    ? `${stepLabel(process)} › ${parameterName}`
                    : parameterName}
                </Link>
              </PropertyLabel>
              <PropertyValue>
                <ErrorMessage>
                  {violations.map((v) => v.message).join(", ")}
                </ErrorMessage>
              </PropertyValue>
            </HorizontalPropertyRow>
          )
        )}

      {hasItemErrors &&
        itemLevelViolations.map((violation, index) => (
          <HorizontalPropertyRow
            key={`item-${violation.bom_path}-${
              violation.constraint_uuid ?? index
            }`}
          >
            <WarningIcon />
            <PropertyLabel>
              <Link
                href="#"
                onClick={(event) => {
                  event.preventDefault();
                  onNavigateToTab("children");
                }}
                style={{
                  textTransform: "capitalize",
                  fontSize: "14px",
                }}
              >
                {(
                  violation.constraint_type ??
                  violation.type_rule ??
                  ""
                ).replaceAll("_", " ")}
              </Link>
            </PropertyLabel>
            <PropertyValue>
              <ErrorMessage>{violation.message}</ErrorMessage>
            </PropertyValue>
          </HorizontalPropertyRow>
        ))}
    </Section>
  );
};

export default ErrorsSection;
